/**
 * The case's rules, one row each, every row under its name (ADR-0100). The first row is the list
 * *Watch* fills: it is read here and changed on the entity itself. A rule of its own can be edited
 * and deleted; an older rule on the default list can only be deleted. Deleting keeps the alerts.
 */
import { Eye, Pencil, Tag, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { EntityOption } from '@/components/entity-picker/EntityPicker'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FilterChipGroup } from '@/components/ui/filter-chip'
import type { RuleSeverity } from '@/services/api/case-monitor'
import { cn } from '@/utils/cn'
import { MONITOR_RULE_KINDS, type MonitorRuleKind } from './AlertInbox'
import { isEditableKind, type RuleDraft } from './RuleForm'
import { useRuleLabel } from './rule-name'
import { SeverityBadge } from './SeverityBadge'

const RULES = 'cases.monitor.rules'

/**
 * Where a rule lives: `watched` is the list *Watch* fills (read here), `default` an older rule on
 * that list (deleted only), `named` a rule on a list of its own (edited and deleted).
 */
export type RuleOrigin = 'watched' | 'default' | 'named'

export interface RuleRow {
  /** The rule's id; null for the watched row while nothing is watched yet. */
  id: string | null
  /** Null names the case's default list, shown in the reader's language. */
  name: string | null
  /** Null for a kind this build does not know (a newer server). */
  kind: MonitorRuleKind | null
  severity: RuleSeverity | null
  /** A phrase rule's words. */
  phrases: readonly string[]
  /** What the rule watches. */
  entities: readonly EntityOption[]
  /** The rule watches every entity the case watches, in place of `entities`. */
  allWatched: boolean
  origin: RuleOrigin
  /** The rule owns its list (a named rule, alone on it), so the form may save it. */
  editable: boolean
}

export interface RulesListActions {
  onEdit: (row: RuleRow) => void
  onDelete: (ruleId: string) => void
  pending: boolean
}

export interface RulesListProps {
  rows: readonly RuleRow[]
  /** The rule an alert pointed at: ringed and scrolled into view. */
  highlightId?: string | null
  /** Null for a reader who may not change the rules (a viewer). */
  actions: RulesListActions | null
}

/** The form's draft for a rule it can edit (one that owns its list, of a type the form makes); else null. */
export function draftOf(row: RuleRow): RuleDraft | null {
  if (!row.editable || !isEditableKind(row.kind)) return null
  return {
    name: row.name ?? '',
    kind: row.kind,
    severity: row.severity ?? 'medium',
    phrasesText: row.phrases.join(', '),
    allWatched: row.allWatched,
    entities: row.entities,
  }
}

function DeleteRule({ name, onDelete }: { name: string; onDelete: () => void }) {
  const { t } = useTranslation()
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={t(`${RULES}.delete`, { name })}
        >
          <Trash2 aria-hidden />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t(`${RULES}.deleteTitle`, { name })}</AlertDialogTitle>
          <AlertDialogDescription>{t(`${RULES}.deleteBody`)}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t(`${RULES}.cancel`)}</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={onDelete}>
            {t(`${RULES}.deleteConfirm`)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/** What the rule watches: its entities and phrases, or that it watches every watched entity. */
export function Watches({ row }: { row: RuleRow }) {
  const { t } = useTranslation()
  if (row.allWatched) {
    return <p className="text-sm text-muted-foreground">{t(`${RULES}.allWatched`)}</p>
  }
  if (row.phrases.length === 0 && row.entities.length === 0) {
    return row.origin === 'watched' ? (
      <p className="text-sm text-muted-foreground">{t(`${RULES}.watchedEmpty`)}</p>
    ) : null
  }
  return (
    <ul className="flex flex-wrap gap-1">
      {row.entities.map(entity => (
        <li key={entity.uid}>
          <Badge variant="outline" className="gap-1">
            <Tag aria-hidden className="size-3 text-muted-foreground" />
            {entity.name}
            {entity.label ? <span className="text-muted-foreground">{entity.label}</span> : null}
          </Badge>
        </li>
      ))}
      {row.phrases.map(phrase => (
        <li key={phrase}>
          <Badge variant="secondary">{phrase}</Badge>
        </li>
      ))}
    </ul>
  )
}

function RuleActions({
  row,
  name,
  actions,
}: {
  row: RuleRow
  name: string
  actions: RulesListActions
}) {
  const { t } = useTranslation()
  const ruleId = row.id
  if (row.origin === 'watched' || ruleId === null) return null
  return (
    <div className="flex shrink-0 items-center">
      {draftOf(row) ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          disabled={actions.pending}
          aria-label={t(`${RULES}.edit`, { name })}
          onClick={() => actions.onEdit(row)}
        >
          <Pencil aria-hidden />
        </Button>
      ) : null}
      <DeleteRule name={name} onDelete={() => actions.onDelete(ruleId)} />
    </div>
  )
}

function Row({
  row,
  actions,
  highlighted,
}: {
  row: RuleRow
  actions: RulesListActions | null
  highlighted: boolean
}) {
  const { t } = useTranslation()
  const name = useRuleLabel()(row.name)
  const note = row.origin === 'named' ? null : t(`${RULES}.notes.${row.origin}`)
  return (
    <li
      ref={node => {
        if (highlighted) node?.scrollIntoView({ block: 'center' })
      }}
      className={cn(
        'flex flex-col gap-2 rounded-md border bg-card px-3 py-2.5',
        row.origin === 'watched' ? 'border-dashed border-border' : 'border-border/60',
        highlighted && 'border-primary/60 ring-2 ring-primary/30'
      )}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            {row.origin === 'watched' ? (
              <Eye aria-hidden className="size-4 shrink-0 text-muted-foreground" />
            ) : null}
            <span className="truncate">{name}</span>
          </h3>
          <p className="text-xs text-muted-foreground">
            {t(`${RULES}.kinds.${row.kind ?? 'unknown'}`)}
          </p>
        </div>
        {row.severity ? <SeverityBadge severity={row.severity} /> : null}
        {actions ? <RuleActions row={row} name={name} actions={actions} /> : null}
      </div>
      {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
      <Watches row={row} />
    </li>
  )
}

/** The kinds the case has rules of, in the order the filter offers them, each with its count. */
function kindCounts(rows: readonly RuleRow[]): { kind: MonitorRuleKind; count: number }[] {
  return MONITOR_RULE_KINDS.map(kind => ({
    kind,
    count: rows.filter(row => row.kind === kind).length,
  })).filter(({ count }) => count > 0)
}

export interface RuleKindFilterProps {
  rows: readonly RuleRow[]
  /** The kind shown; null shows every rule. */
  value: MonitorRuleKind | null
  onChange: (kind: MonitorRuleKind | null) => void
}

/** *All* and one chip per kind the case has, with its count. */
export function RuleKindFilter({ rows, value, onChange }: RuleKindFilterProps) {
  const { t } = useTranslation()
  return (
    <FilterChipGroup<MonitorRuleKind>
      legend={t(`${RULES}.filterLabel`)}
      all={{ label: t(`${RULES}.filterAll`), count: rows.length }}
      options={kindCounts(rows).map(({ kind, count }) => ({
        value: kind,
        label: t(`${RULES}.kindsShort.${kind}`),
        count,
      }))}
      value={value}
      onChange={onChange}
    />
  )
}

export function RulesList({ rows, actions, highlightId = null }: RulesListProps) {
  return (
    <ul className="flex flex-col gap-2">
      {rows.map((row, index) => (
        <Row
          key={row.id ?? `${row.origin}-${index}`}
          row={row}
          actions={actions}
          highlighted={highlightId !== null && row.id === highlightId}
        />
      ))}
    </ul>
  )
}
